import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseDetailsDisclosure } from '@/components/NovoCaso/CaseDetailsDisclosure';
import { CaseFieldsFieldset } from '@/components/NovoCaso/CaseFieldsFieldset';
import { CcbImportField } from '@/components/NovoCaso/CcbImportField';
import { Blueprint, Button, CardKicker } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { ptBR } from '@/i18n/pt-BR';
import { createCase } from '@/lib/cases';
import { type CcbMetadataData } from '@/lib/case_source_documents';
import { useApiClient } from '@/lib/api';
import {
  type FieldErrors,
  type FormState,
  applyCcbMetadata,
  attachImportedCcb,
  buildPayload,
  mapErrorToMessage,
  todayIsoDate,
  validate,
} from './novo_caso_form';

/**
 * Bloco do formulário como objeto de blueprint (E54.F7.T01): kicker mono acima
 * do desenho de linha, no lugar do card sólido de canto arredondado. Os três
 * blocos (identificação, datas, contratante) são a mesma peça.
 */
function FormSection({
  kicker,
  children,
  testId,
}: {
  kicker: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <Blueprint as="section" data-testid={testId} className="p-sc-6">
      <CardKicker className="block">{kicker}</CardKicker>
      <div className="mt-sc-4 grid grid-cols-1 gap-sc-4 sm:grid-cols-2">{children}</div>
    </Blueprint>
  );
}

export function NovoCasoPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const initialDate = useMemo(() => todayIsoDate(), []);
  const [state, setState] = useState<FormState>({
    title: '',
    modalidade: 'grafotecnica',
    requester: '',
    processNumber: '',
    especime: 'Análise do CCB',
    parties: '',
    receivedAt: initialDate,
    deadline: '',
    cpf: '',
    contrato: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // CCB importada: arquivo guardado p/ anexar como contestada no submit (T06)
  // e Identificação/SBR detectada (sem campo direto — exibida p/ conferência).
  const [importedCcb, setImportedCcb] = useState<File | null>(null);
  // Bloco "Mais detalhes": abre sozinho quando há o que conferir (CCB lida) ou
  // o que corrigir (erro de validação num campo de lá).
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);

  function handleChange<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setErrorBanner(null);
  }

  function handleCcbImported(meta: CcbMetadataData, file: File) {
    setState((prev) => applyCcbMetadata(prev, meta));
    setImportedCcb(file);
    setDetalhesAbertos(true);
    setFieldErrors({});
    setErrorBanner(null);
  }

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    await submitCase('continue');
  }

  async function submitCase(target: 'continue' | 'draft') {
    setErrorBanner(null);
    const errors = validate(state);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.processNumber !== undefined || errors.cpf !== undefined) {
        setDetalhesAbertos(true);
      }
      return;
    }
    setSubmitting(true);
    try {
      const record = await createCase(apiClient, buildPayload(state));
      // "Um só upload" (E23.F3.T06): se a CCB foi importada, anexa-a como
      // contestada agora — mesmo handler, sem hand-off cross-página.
      if (importedCcb !== null) {
        const attachError = await attachImportedCcb(apiClient, record.id, importedCcb);
        // F-E23.F3-02: toast durável (sobrevive à navegação imediata) — o banner
        // de página seria desmontado antes de o perito ler. O caso já existe;
        // o perito reenviará a CCB no wizard.
        if (attachError !== null) {
          toast({
            title: ptBR.casos.importCcbAttachFailedTitle,
            description: attachError,
            variant: 'warning',
            duration: 10000,
          });
        }
      }
      const path = target === 'draft' ? '/casos' : `/casos/${record.id}/wizard`;
      navigate(path, { replace: false });
    } catch (err) {
      setErrorBanner(mapErrorToMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      data-testid="novo-caso-page"
      aria-labelledby="novo-caso-title"
      className="mx-auto max-w-4xl"
    >
      <header>
        <p className="mb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
          {ptBR.casos.novoCasoEyebrow}
        </p>
        <h1 id="novo-caso-title" className="font-display text-[34px] leading-none text-fg">
          {ptBR.casos.novoCasoPageTitle}
        </h1>
        <p className="mt-2 max-w-xl text-body-sm text-fg-muted">{ptBR.casos.novoCasoIntro}</p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="mt-[26px] flex flex-col gap-sc-6"
        aria-describedby={errorBanner !== null ? 'novo-caso-error' : undefined}
        noValidate
      >
        {/* A CCB abre o formulário: quando ela existe, é o caminho mais curto —
            preenche os campos e já entra como documento contestado do caso. */}
        <CcbImportField onImported={handleCcbImported} disabled={submitting} />

        <FormSection
          kicker={ptBR.casos.sectionIdentificacao}
          testId="novo-caso-secao-identificacao"
        >
          <CaseFieldsFieldset
            prefix="novo-caso"
            grupo="essenciais"
            state={state}
            errors={fieldErrors}
            disabled={submitting}
            onChange={handleChange}
          />
        </FormSection>

        {/* Aberto quando a CCB preencheu campos daqui — o perito precisa
            conferir o que a leitura propôs, e não confere o que não vê. */}
        <CaseDetailsDisclosure open={detalhesAbertos} onOpenChange={setDetalhesAbertos}>
          <CaseFieldsFieldset
            prefix="novo-caso"
            grupo="detalhes"
            state={state}
            errors={fieldErrors}
            disabled={submitting}
            onChange={handleChange}
          />
        </CaseDetailsDisclosure>

        {errorBanner !== null ? (
          <p
            id="novo-caso-error"
            role="alert"
            data-testid="novo-caso-error"
            className="border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          >
            {errorBanner}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            data-testid="novo-caso-cancel"
            disabled={submitting}
            onClick={() => navigate('/casos')}
          >
            {ptBR.casos.cancel}
          </Button>
          <Button
            type="button"
            variant="secondary"
            data-testid="novo-caso-draft"
            disabled={submitting}
            onClick={() => void submitCase('draft')}
          >
            {submitting ? ptBR.casos.draftSubmitting : ptBR.casos.draftSubmit}
          </Button>
          <Button type="submit" data-testid="novo-caso-submit" disabled={submitting}>
            {submitting ? ptBR.casos.submitting : ptBR.casos.submit}
          </Button>
        </div>
      </form>
    </section>
  );
}
